import { useCallback, useEffect, useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import {
  disableProgramNewsSource,
  enableProgramNewsSource,
  fetchProgramNewsSources,
  setProgramNewsSourceOverride,
  type ProgramSource,
  type ProgramSourceCounts,
  type ProgramSourceCoverage,
  type SetOverrideResponse,
} from "@/services/adminProgramNews";
import OnListToggle from "./OnListToggle";
import SourceStatusCounts, { type SourceFilter } from "./SourceStatusCounts";
import SourceStatusLegend from "./SourceStatusLegend";
import SourceTable from "./SourceTable";
import { mergeToggledSource, reportSourceError } from "./sourceMutation";
import PanelIntro from "./PanelIntro";
import { PANEL_INTROS } from "./panelIntros";

const PAGE_LIMIT = 200;
const EMPTY_COUNTS: ProgramSourceCounts = {
  verified: 0,
  queued: 0,
  failed: 0,
  disabled: 0,
  override_stale: 0,
};

export const READ_ONLY_NOTE =
  "This registry is read-only here. Make fixes in QA; they reach staging and production on the next data push.";

/** The PUT returns the registry row plus its runtime state and override separately; merge them. */
function mergeOverrideResponse(response: SetOverrideResponse): ProgramSource {
  return {
    ...response.source,
    override: response.override ?? response.source.override ?? null,
    state: response.state ?? response.source.state ?? null,
  };
}

function totalOf(counts: ProgramSourceCounts): number {
  return (
    counts.verified + counts.queued + counts.failed + counts.disabled + (counts.override_stale ?? 0)
  );
}

function describeOutcome(updated: ProgramSource): string {
  const attempt = updated.state?.lastAttempt;
  if (updated.status === "verified") {
    return `Verified — ${attempt?.itemCount ?? 0} items from ${updated.url}`;
  }
  const detail = attempt?.error || "No feed found at that URL";
  if (updated.status === "override_stale") return `Override stale: ${detail}`;
  return `Still queued: ${detail}`;
}

const SourcesPanel = () => {
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<SourceFilter>("all");
  const [onListOnly, setOnListOnly] = useState(false);
  const [counts, setCounts] = useState<ProgramSourceCounts>(EMPTY_COUNTS);
  /** null until an API build that reports it responds. */
  const [onListCount, setOnListCount] = useState<number | null>(null);
  const [coverage, setCoverage] = useState<ProgramSourceCoverage | null>(null);
  const [sources, setSources] = useState<ProgramSource[]>([]);
  /** null until the first response tells us which environment we are in. */
  const [writable, setWritable] = useState<boolean | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchProgramNewsSources({
        status: filter === "all" ? undefined : filter,
        limit: PAGE_LIMIT,
        onList: onListOnly || undefined,
      });
      setCounts(response.counts);
      setOnListCount(typeof response.onListCount === "number" ? response.onListCount : null);
      setCoverage(response.coverage ?? null);
      setSources(response.sources);
      setWritable(response.writable === true);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      toast({
        title: "Failed to load program news sources",
        description: message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [filter, onListOnly, toast]);

  useEffect(() => {
    load();
  }, [load]);

  /** Counts always reflect all rows, so a cheap filtered fetch is enough to refresh them. */
  const refreshCounts = useCallback(async () => {
    try {
      const response = await fetchProgramNewsSources({
        status: filter === "all" ? undefined : filter,
        limit: PAGE_LIMIT,
      });
      setCounts(response.counts);
    } catch {
      // Row state is already updated; stale counts are tolerable until the next load.
    }
  }, [filter]);

  const markReadOnly = useCallback(() => setWritable(false), []);

  const handleSetOverride = useCallback(
    async (teamId: string, url: string, reason: string) => {
      try {
        const updated = mergeOverrideResponse(
          await setProgramNewsSourceOverride(teamId, url, reason)
        );
        setSources(prev => prev.map(s => (s.teamId === teamId ? updated : s)));
        toast({ title: "Override set", description: describeOutcome(updated) });
        void refreshCounts();
        return updated;
      } catch (err: unknown) {
        reportSourceError(err, toast, "Override failed", markReadOnly);
        throw err;
      }
    },
    [markReadOnly, refreshCounts, toast]
  );

  const handleDisable = useCallback(
    async (teamId: string, reason: string) => {
      try {
        const { source } = await disableProgramNewsSource(teamId, reason);
        setSources(prev =>
          prev.map(s => (s.teamId === teamId ? mergeToggledSource(s, source) : s))
        );
        toast({ title: "Source disabled", description: `${teamId} will not be collected.` });
        void refreshCounts();
      } catch (err: unknown) {
        reportSourceError(err, toast, "Disable failed", markReadOnly);
        throw err;
      }
    },
    [markReadOnly, refreshCounts, toast]
  );

  const handleEnable = useCallback(
    async (teamId: string) => {
      try {
        const { source } = await enableProgramNewsSource(teamId);
        setSources(prev =>
          prev.map(s => (s.teamId === teamId ? mergeToggledSource(s, source) : s))
        );
        toast({ title: "Source enabled", description: `${teamId} is back in the collection run.` });
        void refreshCounts();
      } catch (err: unknown) {
        reportSourceError(err, toast, "Enable failed", markReadOnly);
        throw err;
      }
    },
    [markReadOnly, refreshCounts, toast]
  );

  return (
    <div className="space-y-4">
      <PanelIntro intro={PANEL_INTROS.sources} />
      {coverage && coverage.workingPct !== null ? (
        <p className="text-sm font-medium" data-testid="school-watch-coverage">
          {`School Watch coverage: ${coverage.working} of ${coverage.listed} programs on athletes' lists have a working news source (${coverage.workingPct}%). Launch needs 90%.`}
        </p>
      ) : null}
      {onListCount !== null ? (
        <p className="text-sm text-muted-foreground">
          {`${onListCount} of ${totalOf(counts)} sources are on an athlete's list`}
        </p>
      ) : null}
      {writable === false ? (
        <p className="text-sm text-muted-foreground">{READ_ONLY_NOTE}</p>
      ) : null}
      <SourceStatusLegend writable={writable === true} />
      <SourceStatusCounts counts={counts} active={filter} onChange={setFilter} />
      <OnListToggle checked={onListOnly} onChange={setOnListOnly} />
      {loading ? (
        <div className="py-8 text-center text-muted-foreground">Loading sources...</div>
      ) : (
        <SourceTable
          sources={sources}
          writable={writable === true}
          onSetOverride={handleSetOverride}
          onDisable={handleDisable}
          onEnable={handleEnable}
        />
      )}
    </div>
  );
};

export default SourcesPanel;
